import { Button, Stack, Typography } from "@mui/material";
import { DocumentFilePicker } from "components/Document/shared/DocumentFilePicker";
import { extensionFromName } from "components/Document/shared/util/extensionFromName";
import { DocumentType } from "generated-graphql/graphql";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useCallback, useRef } from "react";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";

/** The slice of a notice's attached proof document the UI needs. */
export type NoticeProofDocument = {
  id: string;
  title: string;
  fileExtension?: string | null;
};

type UploadedProof = { id: string };

/**
 * Promise-shaped wrapper around `useDocumentUpload` for the save-time proof
 * flow: the file is held locally (in `DocumentFilePicker`) until the notice
 * is saved, then uploaded here and its id handed to the upsert — mirroring
 * `ProofOfContactDialog`. Nothing uploads before save, so cancelling the
 * dialog leaves nothing behind. A save that fails AFTER the upload does
 * leave the uploaded document (referenced by nothing until a retry
 * succeeds); the per-file cache below keeps a retry from minting a second
 * copy of it — the retry reuses the already-uploaded id instead.
 */
export function useNoticeProofUpload() {
  const pendingRef = useRef<{
    resolve: (document: UploadedProof) => void;
    reject: (error: Error) => void;
  } | null>(null);
  // Uploads already completed this mount, keyed by File identity — the same
  // picked file never uploads twice, however many times Save is retried.
  const uploadedRef = useRef(new Map<File, UploadedProof>());

  const { uploadFile, isUploading } = useDocumentUpload({
    onSuccess: (document) => {
      if (document) {
        pendingRef.current?.resolve({ id: document.id });
      } else {
        pendingRef.current?.reject(new Error("Upload returned no document"));
      }
      pendingRef.current = null;
    },
    onError: (error) => {
      pendingRef.current?.reject(error);
      pendingRef.current = null;
    },
  });

  const uploadProof = useCallback(
    async (file: File, tenantId: string): Promise<UploadedProof> => {
      const alreadyUploaded = uploadedRef.current.get(file);
      if (alreadyUploaded) {
        return alreadyUploaded;
      }
      // One settlement slot: a concurrent call would silently strand the
      // in-flight caller's promise, so refuse it loudly instead. The save
      // flows are sequential, so this never fires in normal use.
      if (pendingRef.current) {
        throw new Error("A proof upload is already in flight");
      }
      const result = new Promise<UploadedProof>((resolve, reject) => {
        pendingRef.current = {
          resolve: (document) => {
            uploadedRef.current.set(file, document);
            resolve(document);
          },
          reject,
        };
      });
      try {
        await uploadFile({
          file,
          currentSubmissionDocument: false,
          documentData: {
            tenantId,
            title: file.name,
            fileExtension: extensionFromName(file.name),
            documentType: DocumentType.Proof,
          },
        });
      } catch (error) {
        // uploadFile reports its own failures through onError (which settles
        // `result`), but it can also throw BEFORE its try block — a falsy
        // tenantId — reporting through neither callback. Without this catch
        // that path would leave `result` pending forever and the caller's
        // spinner stuck.
        pendingRef.current = null;
        throw error;
      }
      return result;
    },
    [uploadFile]
  );

  return { uploadProof, isUploading };
}

/**
 * The proof slot inside a notice capture form: the already-attached document
 * (when editing a recorded notice) and the picker holding a local
 * replacement. A newly dropped file supersedes the attached one on save;
 * `onRemoveExisting` (offered only where proof is optional) detaches without
 * a replacement.
 */
export function NoticeProofField({
  existingDocument,
  files,
  onFilesChange,
  onRemoveExisting,
  disabled,
  helperText,
}: {
  existingDocument: NoticeProofDocument | null;
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Omitted where proof is required — there is no "no proof" state to reach. */
  onRemoveExisting?: () => void;
  disabled: boolean;
  helperText: string;
}) {
  return (
    <Stack spacing={1}>
      {existingDocument && files.length === 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
            Attached: {existingDocument.title}
          </Typography>
          {onRemoveExisting && (
            <Button size="small" onClick={onRemoveExisting} disabled={disabled}>
              Remove proof
            </Button>
          )}
        </Stack>
      )}
      <DocumentFilePicker
        files={files}
        onDrop={(dropped) => onFilesChange(dropped.slice(0, 1))}
        onRemove={() => onFilesChange([])}
        canAddMore={files.length === 0}
        maxFiles={1}
        disabled={disabled}
        helperText={helperText}
      />
    </Stack>
  );
}

/**
 * `NoticeProofField` bound to a react-hook-form `File | null` field, for the
 * declaration dialog's capture sections. Shows the field's save-time
 * validation error (there's no MUI input here to carry it) below the picker.
 */
export function NoticeProofFormField<TFieldValues extends FieldValues>({
  control,
  name,
  existingProof,
  helperText,
  onRemoveExisting,
}: {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  existingProof: NoticeProofDocument | null;
  helperText: string;
  /** Omitted where proof is required — there is no "no proof" state to reach. */
  onRemoveExisting?: () => void;
}) {
  const { field, fieldState } = useController({ control, name });
  const file: File | null = field.value ?? null;

  return (
    <Stack spacing={0.5}>
      <NoticeProofField
        existingDocument={existingProof}
        files={file ? [file] : []}
        onFilesChange={(files) => field.onChange(files[0] ?? null)}
        onRemoveExisting={onRemoveExisting}
        disabled={false}
        helperText={helperText}
      />
      {fieldState.error?.message && (
        <Typography variant="caption" color="error">
          {fieldState.error.message}
        </Typography>
      )}
    </Stack>
  );
}
